import React from 'react';
import {useTranslation} from 'react-i18next';
import {isCampaignList, isPlainObject} from '../shapes';
import {formatMoney, formatPercent, isNumeric, prettifyKey} from '../humanize';
import {VALUE_LABELS} from '../labels';
import {extraFieldsSummary, truncateText} from '../diff_field_types';
import {StatGrid} from '../primitives/StatGrid';
import {DetailSection} from '../primitives/DetailSection';
import {DetailFooter} from '../primitives/DetailFooter';

const MAX_CAMPAIGNS = 6;

// El campo que el panel consume por su cuenta (account_id es técnico y ya está en HIDDEN_FIELDS).
const CONSUMED_FIELDS = ['kind', 'account_name', 'limit', 'currency', 'cost', 'budget', 'campaigns'];

const STORY_KEYS = {
    cost: 'activity_log.detail.daily_limit_story_cost',
    account_budget: 'activity_log.detail.daily_limit_story_account_budget',
    campaign_budget: 'activity_log.detail.daily_limit_story_campaign_budget',
};

function stringOrNull(value) {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

// El monto observado: consumo del día en 'cost', suma de presupuestos diarios en el resto.
// Con un kind inesperado se usa el que haya venido.
function observedAmount(detail, kind) {
    const primary = kind === 'cost' ? detail.cost : detail.budget;
    if (isNumeric(primary)) return primary;
    const secondary = kind === 'cost' ? detail.budget : detail.cost;
    return isNumeric(secondary) ? secondary : primary;
}

// Cuánto se pasó del límite: flecha ↑ + monto + (%). El tono lo pone el StatGrid ('bad'):
// es una advertencia operativa, no una alerta crítica.
function excessText(observed, limit, currency) {
    if (!isNumeric(observed) || !isNumeric(limit)) return null;
    const excess = Number(observed) - Number(limit);
    if (excess <= 0) return null;
    const percent = Number(limit) !== 0
        ? formatPercent((excess / Math.abs(Number(limit))) * 100, {signed: true})
        : null;
    return `↑ ${formatMoney(excess, currency)}${percent ? ` (${percent})` : ''}`;
}

// `converted` ya viene formateada por el backend (incluye la conversión de moneda);
// `daily_budget` llega cruda y se formatea con la moneda del detail.
function campaignAmount(campaign, currency) {
    const converted = stringOrNull(campaign.converted);
    if (converted) return truncateText(converted);
    const raw = campaign.daily_budget;
    if (raw === null || raw === undefined || raw === '') return null;
    return isNumeric(raw) ? formatMoney(raw, currency) : truncateText(String(raw));
}

function kindLabel(kind, t) {
    if (!kind) return null;
    return VALUE_LABELS[kind] ? t(VALUE_LABELS[kind]) : prettifyKey(kind);
}

function CampaignList({campaigns, currency, t}) {
    const shown = campaigns.slice(0, MAX_CAMPAIGNS);
    const rest = campaigns.length - shown.length;
    return (
        <ul className="ald-list">
            {shown.map((campaign, index) => {
                const amount = campaignAmount(campaign, currency);
                return (
                    <li key={index}>
                        <span className="ald-list-name">{stringOrNull(campaign.name)}</span>
                        {amount ? <span className="ald-list-value">{amount}</span> : null}
                    </li>
                );
            })}
            {rest > 0 ? <li className="ald-list-more">{t('activity_log.detail.more_items', {count: rest})}</li> : null}
        </ul>
    );
}

// DAILY_LIMIT_EXCEEDED: la frase dice qué pasó según el `kind` (consumo del día vs suma de
// presupuestos diarios), los mini-stats dan los tres números que importan (observado /
// límite / exceso) y la lista muestra las campañas que lo empujaron.
export function DailyLimitDetail({detail}) {
    const {t} = useTranslation();
    if (!isPlainObject(detail)) return null;

    const kind = stringOrNull(detail.kind);
    const currency = stringOrNull(detail.currency);
    const account = stringOrNull(detail.account_name);
    const observed = observedAmount(detail, kind);
    const limit = detail.limit;

    const observedValue = isNumeric(observed) ? formatMoney(observed, currency) : null;
    const limitValue = isNumeric(limit) ? formatMoney(limit, currency) : null;
    const excess = excessText(observed, limit, currency);

    const storyKey = (kind && STORY_KEYS[kind]) || 'activity_log.detail.daily_limit_story_generic';
    const tellsFullStory = !!account && !!limitValue && !!observedValue && !!(kind && STORY_KEYS[kind]);
    const story = account && limitValue
        ? t(tellsFullStory ? storyKey : 'activity_log.detail.daily_limit_story_generic', {
            account,
            cost: observedValue,
            budget: observedValue,
            limit: limitValue,
            interpolation: {escapeValue: false},
        })
        : null;

    const stats = [
        {
            key: 'observed',
            label: t(kind === 'cost'
                ? 'activity_log.detail.stat_spent_today'
                : 'activity_log.detail.stat_daily_budget_sum'),
            value: observedValue,
        },
        {key: 'limit', label: t('activity_log.field.daily_limit_value'), value: limitValue},
        {key: 'excess', label: t('activity_log.detail.stat_excess'), value: excess, tone: 'bad'},
    ];
    // Sin frase (falta la cuenta o el kind es inesperado) el tipo de límite se muestra como
    // stat: el dato no se pierde en silencio.
    if (!tellsFullStory) stats.unshift({key: 'kind', label: t('activity_log.field.kind'), value: kindLabel(kind, t)});

    // Ítems sin nombre utilizable no se listan (nunca "(vacío)" en la lista).
    const namedCampaigns = isCampaignList(detail.campaigns)
        ? detail.campaigns.filter(campaign => stringOrNull(campaign.name))
        : [];
    const campaigns = namedCampaigns.length ? namedCampaigns : null;
    const extras = extraFieldsSummary(detail, t, CONSUMED_FIELDS);

    return (
        <React.Fragment>
            {story ? <div className="ald-story">{story}</div> : null}
            <StatGrid stats={stats}/>
            {campaigns ? (
                <DetailSection title={t('activity_log.detail.campaigns_daily_budget')}>
                    <CampaignList campaigns={campaigns} currency={currency} t={t}/>
                </DetailSection>
            ) : null}
            <DetailFooter>{extras}</DetailFooter>
        </React.Fragment>
    );
}
